import { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Stack,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import {
  parseEntryData,
  useTaxonomyAssociations,
  useTaxonomyEntry,
  type FieldSchemaEntry,
} from '../../api/queries/taxonomy';

interface Props {
  entryId: string | null;
  open: boolean;
  onClose: () => void;
  onCopyToWorld?: (entryIds: string[]) => void;
  onNavigateToEntry?: (entryId: string) => void;
  fieldSchema?: FieldSchemaEntry[];
}

export function TaxonomyEntryDetail({
  entryId,
  open,
  onClose,
  onCopyToWorld,
  onNavigateToEntry,
  fieldSchema,
}: Props) {
  const { data: entry, isLoading } = useTaxonomyEntry(entryId);
  const { data: associations = [] } = useTaxonomyAssociations(entryId);

  const parsed = useMemo(
    () => (entry ? parseEntryData(entry.data) : {}),
    [entry],
  );

  // Group associations by type
  const groupedAssociations = useMemo(() => {
    const groups = new Map<string, typeof associations>();
    for (const a of associations) {
      const list = groups.get(a.associationType) ?? [];
      list.push(a);
      groups.set(a.associationType, list);
    }
    return groups;
  }, [associations]);

  // Determine which fields to display, in the order defined by the schema
  const displayFields = useMemo(() => {
    if (!entry) return [];
    const schema = fieldSchema ?? [];
    const data = parsed;
    const fields: { label: string; value: unknown; type: string }[] = [];

    // If we have a schema, use it to order fields
    if (schema.length > 0) {
      for (const f of schema) {
        if (f.name === 'name') continue; // skip name, shown as title
        const val = data[f.name];
        if (val !== undefined && val !== null && val !== '') {
          fields.push({
            label: f.description || f.name,
            value: val,
            type: f.type,
          });
        }
      }
    } else {
      // Fallback: display all string/array fields from the data
      for (const [key, val] of Object.entries(data)) {
        if (key === 'name') continue;
        if (val !== undefined && val !== null && val !== '') {
          const type = Array.isArray(val) ? 'array<string>' : typeof val;
          const label = key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
          fields.push({ label, value: val, type });
        }
      }
    }
    return fields;
  }, [entry, parsed, fieldSchema]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 0 } }}
    >
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {entry && (
        <Stack sx={{ height: '100%' }}>
          {/* Header */}
          <Box sx={{ p: 3, pb: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              {entry.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {entry.categoryName}
            </Typography>
          </Box>

          <Divider />

          {/* Scrollable content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <Stack spacing={2.5}>
              {displayFields.map((field) => (
                <Box key={field.label}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ fontSize: 11, letterSpacing: 0.8 }}
                  >
                    {field.label}
                  </Typography>
                  {renderFieldValue(field.value, field.type)}
                </Box>
              ))}

              {/* Associations */}
              {groupedAssociations.size > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <LinkIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2">Related Entries</Typography>
                  </Stack>
                  {Array.from(groupedAssociations.entries()).map(
                    ([assocType, items]) => (
                      <Box key={assocType}>
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          sx={{ fontSize: 11 }}
                        >
                          {formatAssociationType(assocType)}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
                        >
                          {items.map((a) => (
                            <Chip
                              key={a.id}
                              label={a.relatedEntryName}
                              size="small"
                              clickable
                              onClick={() =>
                                onNavigateToEntry?.(a.relatedEntryId)
                              }
                            />
                          ))}
                        </Stack>
                      </Box>
                    ),
                  )}
                </>
              )}
            </Stack>
          </Box>

          {/* Action footer */}
          {onCopyToWorld && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<ContentCopyIcon />}
                  fullWidth
                  onClick={() => onCopyToWorld([entry.id])}
                >
                  Copy to World
                </Button>
              </Box>
            </>
          )}
        </Stack>
      )}
    </Drawer>
  );
}

function renderFieldValue(value: unknown, _type: string) {
  if (Array.isArray(value)) {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
      >
        {(value as string[]).map((item, i) => (
          <Chip key={i} label={String(item)} size="small" variant="outlined" />
        ))}
      </Stack>
    );
  }

  if (typeof value === 'string') {
    return (
      <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>
        {value}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" sx={{ mt: 0.25 }}>
      {JSON.stringify(value)}
    </Typography>
  );
}

function formatAssociationType(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
